import * as React from "react";
import {
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Container,
  CssBaseline,
  Grid,
  Toolbar,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SchemaIcon from "@mui/icons-material/Schema";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import AdminPageHeader from "../components/AdminPageHeader";
import { useApi } from "../hooks/useApi";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

export default function AdminUsers() {
  const api = useApi();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [pagination, setPagination] = React.useState({
    total: 0,
    limit: 20,
    offset: 0,
    page: 1,
  });
  const [schemaDialogOpen, setSchemaDialogOpen] = React.useState(false);
  const [schemaTableName, setSchemaTableName] = React.useState("");
  const [schemaData, setSchemaData] = React.useState(null);
  const [loadingSchema, setLoadingSchema] = React.useState(false);
  const [schemaError, setSchemaError] = React.useState(null);

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["users", debouncedSearch, pagination.offset, pagination.limit],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (debouncedSearch?.trim()) params.append("search", debouncedSearch.trim());
      params.append("limit", pagination.limit.toString());
      params.append("offset", pagination.offset.toString());
      return api.get(`/admin/users?${params.toString()}`);
    },
  });

  const users = data?.data ?? [];
  const total = data?.pagination?.total ?? 0;
  const loading = isLoading;

  const handleViewUser = (userId) => {
    navigate(`/dashboard/users/${userId}`);
  };
  
  const handlePageChange = (newPage) => {
    const newOffset = (newPage - 1) * pagination.limit;
    setPagination((prev) => ({ ...prev, offset: newOffset, page: newPage }));
  };

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setPagination((prev) => ({ ...prev, limit: newLimit, offset: 0, page: 1 }));
  };

  const totalPages = Math.ceil(total / pagination.limit);

  const handleCloseSchemaDialog = () => {
    setSchemaDialogOpen(false);
    setSchemaTableName("");
    setSchemaData(null);
    setSchemaError(null);
  };

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar sx={{ minHeight: "48px !important", height: "48px" }} />
          <Container maxWidth="xl" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  onClick={() => navigate("/dashboard")}
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Admin
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  Users
                </Typography>
              </Breadcrumbs>
            </Box>

            <AdminPageHeader
              title="Users"
              description="Manage all users in the system"
              icon={<PeopleIcon sx={{ color: "primary.main" }} />}
            >
              <Button
                variant="contained"
                sx={{ bgcolor: "white", color: "black", "&:hover": { bgcolor: "grey.100" } }}
                startIcon={<SchemaIcon />}
                onClick={async () => {
                  setSchemaDialogOpen(true);
                  setSchemaTableName("users");
                  setSchemaData(null);
                  setSchemaError(null);
                  setLoadingSchema(true);
                  setSchemaError(null);
                  try {
                    const data = await api.get("/admin/schema/users");
                    setSchemaData(data);
                  } catch (err) {
                    console.error("Error fetching schema:", err);
                    setSchemaError(err.message || "Failed to fetch schema");
                  } finally {
                    setLoadingSchema(false);
                  }
                }}
              >
                Schema
              </Button>
            </AdminPageHeader>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error.message || "Failed to fetch users"}
              </Alert>
            )}

            <Card>
              <CardContent>
                {/* Top pagination controls */}
                {!loading && pagination.total > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        Showing {pagination.offset + 1}-
                        {Math.min(pagination.offset + pagination.limit, total)} of {total} users
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Per Page</InputLabel>
                        <Select
                          value={pagination.limit}
                          onChange={handleLimitChange}
                          label="Per Page"
                          disabled={loading}
                        >
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={20}>20</MenuItem>
                          <MenuItem value={50}>50</MenuItem>
                          <MenuItem value={100}>100</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    {totalPages > 1 && (
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handlePageChange(pagination.page - 1)}
                          disabled={pagination.page <= 1 || loading}
                        >
                          Previous
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                          Page {pagination.page} of {totalPages} ({total} total)
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handlePageChange(pagination.page + 1)}
                          disabled={pagination.page >= totalPages || loading}
                        >
                          Next
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
                
                <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center" }}>
                  <TextField
                    fullWidth
                    placeholder="Search users by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Updated</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        Array.from({ length: 5 }).map((_, index) => (
                          <TableRow key={`skeleton-${index}`}>
                            <TableCell>
                              <Skeleton height={20} />
                            </TableCell>
                            <TableCell>
                              <Skeleton height={20} />
                            </TableCell>
                            <TableCell>
                              <Skeleton height={20} width={100} />
                            </TableCell>
                            <TableCell>
                              <Skeleton height={20} width={100} />
                            </TableCell>
                            <TableCell align="right">
                              <Skeleton height={32} width={80} />
                            </TableCell>
                          </TableRow>
                        ))
                      ) : users.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography color="text.secondary">
                              {searchQuery ? "No users found matching your search" : "No users found"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        users.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>{user.name || "N/A"}</TableCell>
                            <TableCell>{user.email || "N/A"}</TableCell>
                            <TableCell>
                              {user.created_at
                                ? new Date(user.created_at).toLocaleDateString()
                                : "N/A"}
                            </TableCell>
                            <TableCell>
                              {user.updated_at
                                ? new Date(user.updated_at).toLocaleDateString()
                                : "N/A"}
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<VisibilityIcon />}
                                onClick={() => handleViewUser(user.id)}
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {!loading && pagination.total > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        Showing {pagination.offset + 1}-
                        {Math.min(pagination.offset + pagination.limit, total)} of {total} users
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Per Page</InputLabel>
                        <Select
                          value={pagination.limit}
                          onChange={handleLimitChange}
                          label="Per Page"
                          disabled={loading}
                        >
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={20}>20</MenuItem>
                          <MenuItem value={50}>50</MenuItem>
                          <MenuItem value={100}>100</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    {totalPages > 1 && (
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handlePageChange(pagination.page - 1)}
                          disabled={pagination.page <= 1 || loading}
                        >
                          Previous
                        </Button>
                        <Typography variant="body2" color="text.secondary">
                          Page {pagination.page} of {totalPages} ({total} total)
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handlePageChange(pagination.page + 1)}
                          disabled={pagination.page >= totalPages || loading}
                        >
                          Next
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Container>
          <Footer />
        </Box>
      </Box>

      {/* Schema Dialog */}
      <Dialog
        open={schemaDialogOpen}
        onClose={handleCloseSchemaDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Table Schema</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ mb: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Table Name</InputLabel>
              <Select
                value={schemaTableName}
                onChange={(e) => {
                  const tableName = e.target.value;
                  setSchemaTableName(tableName);
                  if (tableName) {
                    const fetchSchema = async () => {
                      setLoadingSchema(true);
                      setSchemaError(null);
                      setSchemaData(null);
                      try {
                        const data = await api.get(`/admin/schema/${tableName}`);
                        setSchemaData(data);
                      } catch (err) {
                        console.error("Error fetching schema:", err);
                        setSchemaError(err.message || "Failed to fetch schema");
                      } finally {
                        setLoadingSchema(false);
                      }
                    };
                    fetchSchema();
                  }
                }}
                label="Table Name"
              >
                <MenuItem value="organizations">organizations</MenuItem>
                <MenuItem value="users">users</MenuItem>
                <MenuItem value="subscriptions">subscriptions</MenuItem>
                <MenuItem value="orders">orders</MenuItem>
                <MenuItem value="products">products</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {schemaError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {schemaError}
            </Alert>
          )}

          {loadingSchema && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {schemaData && (
            <Box>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Table: {schemaData.table_name}
              </Typography>
              
              {/* Columns */}
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                Columns
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Nullable</TableCell>
                      <TableCell>Default</TableCell>
                      <TableCell>Position</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {schemaData.columns.map((col) => (
                      <TableRow key={col.name}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={schemaData.primary_keys?.includes(col.name) ? "bold" : "normal"}>
                            {col.name}
                            {schemaData.primary_keys?.includes(col.name) && " (PK)"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {col.type}
                            {col.max_length && `(${col.max_length})`}
                            {col.precision && col.scale && `(${col.precision},${col.scale})`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={col.nullable ? "YES" : "NO"}
                            size="small"
                            color={col.nullable ? "default" : "warning"}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                            {col.default || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>{col.position}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Foreign Keys */}
              {schemaData.foreign_keys && schemaData.foreign_keys.length > 0 && (
                <>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                    Foreign Keys
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Column</TableCell>
                          <TableCell>References Table</TableCell>
                          <TableCell>References Column</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {schemaData.foreign_keys.map((fk, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{fk.column}</TableCell>
                            <TableCell>{fk.references_table}</TableCell>
                            <TableCell>{fk.references_column}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* Unique Constraints */}
              {schemaData.unique_constraints && schemaData.unique_constraints.length > 0 && (
                <>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                    Unique Constraints
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    {schemaData.unique_constraints.map((uc, idx) => (
                      <Chip
                        key={idx}
                        label={`${uc.column} (${uc.constraint_name})`}
                        size="small"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                </>
              )}

              {/* Indexes */}
              {schemaData.indexes && schemaData.indexes.length > 0 && (
                <>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                    Indexes
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Definition</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {schemaData.indexes.map((idx) => (
                          <TableRow key={idx.name}>
                            <TableCell>
                              <Typography variant="body2" fontFamily="monospace">
                                {idx.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                                {idx.definition}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* Raw JSON View */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                  Raw JSON
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.75rem",
                      overflow: "auto",
                      maxHeight: "300px",
                      m: 0,
                    }}
                  >
                    {JSON.stringify(schemaData, null, 2)}
                  </Typography>
                </Paper>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSchemaDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
